// Temporada global de la aplicación.
// Una única temporada seleccionada, compartida por todas las pantallas y
// persistida en localStorage. Las temporadas disponibles vienen del backend.
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import apiFetch from "services/http";

const STORAGE_KEY = "convokko_temporada";
// Última temporada "actual" (sugerida por el backend) que vio este navegador.
// Sirve para distinguir "el usuario eligió ver una temporada antigua a
// propósito" de "se migró de temporada y hay que saltar a la nueva sí o sí".
const STORAGE_ACTUAL_KEY = "convokko_temporada_actual_conocida";

const SeasonContext = createContext(null);

export function SeasonProvider({ children }) {
  const [temporadas, setTemporadas] = useState([]);
  const [temporada, setTemporadaState] = useState(() => {
    const saved = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(saved) && saved > 2000 ? saved : null;
  });
  const [cargando, setCargando] = useState(true);

  const refrescarTemporadas = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token || token === "undefined" || token === "null") {
        // Sin sesión: no se consulta el backend (evita redirecciones en el login)
        const anio = new Date().getFullYear();
        setTemporadas((prev) => (prev.length ? prev : [anio]));
        setTemporadaState((prev) => prev || anio);
        setCargando(false);
        return;
      }
      const data = await apiFetch("/api/temporadas");
      const lista = Array.isArray(data?.temporadas) ? data.temporadas.map(Number) : [];
      const actualBackend = Number(data?.actual) || lista[0] || new Date().getFullYear();
      setTemporadas(lista);

      // Si el backend sugiere una temporada "actual" distinta a la última que
      // conocíamos, es que se ha migrado de temporada: forzamos el salto para
      // que todos los entrenadores vean la nueva por defecto, aunque tuvieran
      // otra guardada de una sesión anterior.
      const actualConocidaPrevia = Number(localStorage.getItem(STORAGE_ACTUAL_KEY));
      const huboMigracion =
        Number.isFinite(actualConocidaPrevia) && actualConocidaPrevia !== actualBackend;

      setTemporadaState((prev) => {
        const elegida = !huboMigracion && prev && lista.includes(prev) ? prev : actualBackend;
        localStorage.setItem(STORAGE_KEY, String(elegida));
        return elegida;
      });
      localStorage.setItem(STORAGE_ACTUAL_KEY, String(actualBackend));
    } catch (e) {
      // Sin backend de temporadas disponible: año actual como única opción
      const anio = new Date().getFullYear();
      setTemporadas((prev) => (prev.length ? prev : [anio]));
      setTemporadaState((prev) => prev || anio);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    refrescarTemporadas();
  }, [refrescarTemporadas]);

  const setTemporada = useCallback((valor) => {
    const num = Number(valor);
    if (Number.isFinite(num)) {
      localStorage.setItem(STORAGE_KEY, String(num));
      setTemporadaState(num);
    }
  }, []);

  const value = useMemo(
    () => ({ temporada, setTemporada, temporadas, cargando, refrescarTemporadas }),
    [temporada, setTemporada, temporadas, cargando, refrescarTemporadas]
  );

  return <SeasonContext.Provider value={value}>{value ? children : null}</SeasonContext.Provider>;
}

SeasonProvider.propTypes = {
  children: PropTypes.node,
};

export function useSeason() {
  const ctx = useContext(SeasonContext);
  if (!ctx) {
    // Permite usar componentes fuera del provider sin romper (p.ej. tests)
    return {
      temporada: new Date().getFullYear(),
      setTemporada: () => {},
      temporadas: [new Date().getFullYear()],
      cargando: false,
      refrescarTemporadas: () => {},
    };
  }
  return ctx;
}

export default SeasonContext;
